<template>
    <widget-card
        colClasses="xs12"
        :heading="chartTitle"
        custom-card-classes="chart-wrapper">
        <v-select
            :items="priceTypeOptions"
            v-model="selectedPriceType"
            box
            label="Price Type"
            class="mb-2"></v-select>
        <div style="height: 280px;">
        <pie-chart-channels 
                :key="chartKey"
            :values="values" 
            :is-money-value="isMoneyValue" 
            :styles="myStyles">
        </pie-chart-channels>
        </div>
        <div class="chart-summary mt-3">
            <div class="summary-row">
                <span class="summary-label">All:</span>
                <span class="summary-value">{{ formatDisplayValue(summary.all) }}</span>
            </div>
            <div class="summary-row">
                <span class="summary-label">Unused:</span>
                <span class="summary-value">{{ formatDisplayValue(summary.unused) }}</span>
            </div>
            <div class="summary-row">
                <span class="summary-label">Used:</span>
                <span class="summary-value">{{ formatDisplayValue(summary.used) }}</span>
            </div>
        </div>
    </widget-card>
</template>

<script>
import Enumerable from 'linq'
import PieChartChannels from './PieChartChannels.js'
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'PieChartChannelsArrangementFee',
    components: {
        PieChartChannels
    },
    props: {
        widget: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            totalValue: 0,
            chartKey: 0,
            selectedPriceType: 'arrangement_fee',
            priceTypeOptions: [
                { text: 'Cost Price', value: 'cost_price' },
                { text: 'Sales Price', value: 'sales_price' },
                { text: 'Arrangement Fee', value: 'arrangement_fee' }
            ]
        };
    },
    created() {
        // Stelle sicher, dass die Event-Targets geladen werden
        this.getEventTargets();
        
        // Priorität: 1. widget.data.priceType, 2. Voreinstellung 'arrangement_fee'
        if (this.widget.data && this.widget.data.priceType) {
            this.selectedPriceType = this.widget.data.priceType;
        }
    },
    computed: {
        ...mapGetters([
            'season',
            'reportingData',
            'lastSeasonReportingData',
            'selectedF1Event',
            'eventGroupsForCurrentSeason',
            'eventTargets'
        ]),
        chartTitle() {
            return this.widget.text.caption[this.$i18n.locale];
        },
        myStyles() {
            return {
                height: `85%`,
                position: 'relative'
            }
        },
        values() {
            // Theoretische Kosten berechnen (Target Sales × gewählter Preistyp)
            let theoreticalCosts = 0;
            let actualCostsByPartner = {};
            
            // Filtere die relevanten Daten basierend auf dem ausgewählten Event
            const filteredData = this.reportingData.filter(item => {
                // Prüfe das ausgewählte Event
                if (this.selectedF1Event === 'all') return true;
                return item.event_group_id === parseInt(this.selectedF1Event);
            }).filter(item => 
                // Nur Multi-Day Tickets berücksichtigen
                item.FK_ArticleType === 1 || item.FK_ArticleType === 2
            ).filter(item => 
                // Nur Datensätze mit gefülltem wo_partner_name berücksichtigen
                item.wo_partner_name && item.wo_partner_name.trim() !== ''
            );

            console.log("Filtered Data Length:", filteredData.length);

            // Debug: Überprüfe, ob die Datenfelder vorhanden sind
            if (filteredData.length > 0) {
                const sampleItem = filteredData[0];
                console.log("Sample Data Item Fields:", {
                    costs_total: sampleItem.costs_total,
                    sales_total: sampleItem.sales_total,
                    arrangement_fee: sampleItem.arrangement_fee,
                    costs: sampleItem.costs,
                    sales: sampleItem.sales,
                    arrangement_fee_per_ticket: sampleItem.arrangement_fee_per_ticket,
                    Menge: sampleItem.Menge
                });
                
                // Berechne und zeige Beispielwerte pro Ticket an
                if (sampleItem.sales_total && sampleItem.Menge) {
                    console.log("Sample sales_price per ticket:", parseFloat(sampleItem.sales_total) / parseFloat(sampleItem.Menge));
                }
                if (sampleItem.arrangement_fee && sampleItem.Menge) {
                    console.log("Sample arrangement_fee per ticket:", parseFloat(sampleItem.arrangement_fee) / parseFloat(sampleItem.Menge));
                }
            }

            if (filteredData.length === 0) {
                console.log("No filtered data found");
                return [];
            }
            
            // Berechne die tatsächlichen Kosten pro Partner
            let investorsCosts = 0;
            let partnershipSalesCosts = 0;
            let otherPartnersCosts = 0;

            filteredData.forEach(item => {
                const eventId = String(item.event_group_id);
                const partnerName = item.wo_partner_name;
                
                // Verwende das entsprechende Kostenfeld basierend auf der Auswahl
                let costValue = 0;
                
                if (this.selectedPriceType === 'cost_price') {
                    // Bei cost_price wird der Gesamtwert aus costs_total verwendet
                    costValue = item.costs_total && parseFloat(item.costs_total) > 0 ? parseFloat(item.costs_total) : 0;
                } else if (this.selectedPriceType === 'sales_price') {
                    // Bei sales_price verwenden wir den Gesamtwert aus sales_total
                    costValue = item.sales_total && parseFloat(item.sales_total) > 0 ? parseFloat(item.sales_total) : 
                               (item.sales && parseFloat(item.sales) > 0 && item.Menge && parseFloat(item.Menge) > 0 ? 
                                parseFloat(item.sales) * parseFloat(item.Menge) : 0);
                } else if (this.selectedPriceType === 'arrangement_fee') {
                    // Bei arrangement_fee benötigen wir den Einzelpreis pro Ticket multipliziert mit der Menge
                    costValue = item.arrangement_fee_per_ticket && parseFloat(item.arrangement_fee_per_ticket) > 0 && item.Menge && parseFloat(item.Menge) > 0 ? 
                                parseFloat(item.arrangement_fee_per_ticket) * parseFloat(item.Menge) : 
                               (item.arrangement_fee && parseFloat(item.arrangement_fee) > 0 ? parseFloat(item.arrangement_fee) : 0);
                }
                
                if (costValue > 0) {
                    // Gruppiere die Kosten nach den drei Kategorien
                    if (partnerName === 'Investors') {
                        investorsCosts += costValue;
                    } else if (partnerName === 'Partnership Sales') {
                        partnershipSalesCosts += costValue;
                    } else {
                        otherPartnersCosts += costValue;
                    }
                }
            });

            console.log("==== PARTNER COSTS SUMMARY ====");
            console.log("Investors Costs:", investorsCosts);
            console.log("Partnership Sales Costs:", partnershipSalesCosts);
            console.log("Other Partners Costs:", otherPartnersCosts);
            console.log("Total Partner Costs:", investorsCosts + partnershipSalesCosts + otherPartnersCosts);

            // Wenn keine tatsächlichen Kosten berechnet werden konnten, können wir kein Chart anzeigen
            if (investorsCosts + partnershipSalesCosts + otherPartnersCosts === 0) {
                console.log("No actual costs could be calculated, cannot display chart");
                return [];
            }
            
            // Finde den Preis pro Ticket aus den Daten basierend auf dem ausgewählten Preistyp
            let fixedPrice = 0;
            
            // Suche nach einem gültigen Preiswert in den gefilterten Daten
            for (const item of filteredData) {
                let priceField = '';
                let alternativeField = '';
                
                if (this.selectedPriceType === 'cost_price') {
                    priceField = 'costs';
                    alternativeField = 'costs_total';
                } else if (this.selectedPriceType === 'sales_price') {
                    priceField = 'sales';
                    alternativeField = 'sales_total';
                } else if (this.selectedPriceType === 'arrangement_fee') {
                    priceField = 'arrangement_fee_per_ticket';
                    alternativeField = 'arrangement_fee';
                }
                
                if (item[priceField] && parseFloat(item[priceField]) > 0) {
                    fixedPrice = parseFloat(item[priceField]);
                    console.log(`Using fixed ${this.selectedPriceType} from data field ${priceField}: ${fixedPrice}`);
                    break;
                } else if (alternativeField && item[alternativeField] && parseFloat(item[alternativeField]) > 0) {
                    let value = parseFloat(item[alternativeField]);
                    
                    // Bei arrangement_fee benötigen wir die Division durch die Menge, bei den anderen nicht
                    if (this.selectedPriceType === 'arrangement_fee' && item.Menge && parseFloat(item.Menge) > 0) {
                        value = value / parseFloat(item.Menge); // Berechne Preis pro Ticket
                    }
                    
                    fixedPrice = value;
                    console.log(`Using fixed ${this.selectedPriceType} from alternative data field ${alternativeField}: ${fixedPrice}`);
                    break;
                }
            }
            
            // Wenn kein Wert gefunden wurde, können wir keine theoretischen Kosten berechnen
            if (fixedPrice <= 0) {
                console.log(`No valid fixed ${this.selectedPriceType} found in the data`);
                return [];
            }
            
            console.log(`FINAL FIXED PRICE FOR ${this.selectedPriceType}: ${fixedPrice}`);

            // Berechne die theoretischen Kosten (Target Sales × gewählter Preistyp)
            if (this.selectedF1Event === 'all') {
                console.log(`==== CALCULATING THEORETICAL ${this.selectedPriceType.toUpperCase()} FOR ALL EVENTS ====`);
                // Für alle Events: Verarbeite jedes Event mit dem entsprechenden Preis
                if (this.eventTargets && this.eventTargets.length > 0) {
                    // Erstelle eine Map der Preise pro Event
                    const priceByEvent = {};
                    
                    // Ermittle den Preis für jedes Event
                    this.reportingData.forEach(item => {
                        let priceField = '';
                        let alternativeField = '';
                        
                        if (this.selectedPriceType === 'cost_price') {
                            priceField = 'costs';
                            alternativeField = 'costs_total';
                        } else if (this.selectedPriceType === 'sales_price') {
                            priceField = 'sales';
                            alternativeField = 'sales_total';
                        } else if (this.selectedPriceType === 'arrangement_fee') {
                            priceField = 'arrangement_fee_per_ticket';
                            alternativeField = 'arrangement_fee';
                        }
                        
                        // Prüfe primäres Feld oder Alternativfeld
                        if ((item[priceField] && parseFloat(item[priceField]) > 0) && 
                           (item.FK_ArticleType === 1 || item.FK_ArticleType === 2)) {
                            const eventId = String(item.event_group_id);
                            if (!priceByEvent[eventId]) {
                                priceByEvent[eventId] = parseFloat(item[priceField]);
                            }
                        } else if (alternativeField && item[alternativeField] && parseFloat(item[alternativeField]) > 0 &&
                                  (item.FK_ArticleType === 1 || item.FK_ArticleType === 2)) {
                            const eventId = String(item.event_group_id);
                            if (!priceByEvent[eventId]) {
                                let value = parseFloat(item[alternativeField]);
                                // Beim Berechnen des Preis pro Ticket nur für arrangement_fee durch die Menge teilen
                                if (this.selectedPriceType === 'arrangement_fee' && item.Menge && parseFloat(item.Menge) > 0) {
                                    value = value / parseFloat(item.Menge); // Berechne Preis pro Ticket
                                }
                                priceByEvent[eventId] = value;
                            }
                        }
                    });
                    
                    console.log(`${this.selectedPriceType} by event:`, priceByEvent);
                    
                    // Berechne die theoretischen Kosten für jedes Event mit dem entsprechenden Preis
                    this.eventTargets.forEach(target => {
                        const targetSales = parseFloat(target.TargetSales || 0);
                        const eventId = String(target.FK_EventGroup);
                        
                        // Verwende den event-spezifischen Preis oder den Standard-Preis
                        const eventPrice = priceByEvent[eventId] || fixedPrice;
                        
                        // Nur berechnen, wenn ein gültiger TargetSales-Wert vorhanden ist
                        if (targetSales > 0) {
                            // Berechne die theoretischen Kosten mit dem event-spezifischen Preis
                            const eventTheoreticalCosts = targetSales * eventPrice;
                            theoreticalCosts += eventTheoreticalCosts;
                            console.log(`Event ${eventId}: Target Sales = ${targetSales}, Event ${this.selectedPriceType} = ${eventPrice}, Theoretical ${this.selectedPriceType} += ${eventTheoreticalCosts}`);
                        }
                    });
                }
            } else {
                console.log(`==== CALCULATING THEORETICAL ${this.selectedPriceType.toUpperCase()} FOR SELECTED EVENT ====`);
                // Für ein spezifisches Event: Target Sales des Events × fester Preis
                const eventTarget = this.eventTargets && this.eventTargets.length > 0 ? 
                    this.eventTargets.find(target => String(target.FK_EventGroup) === String(this.selectedF1Event)) : null;
                
                if (eventTarget) {
                    const targetSales = parseFloat(eventTarget.TargetSales || 0);
                    
                    // Nur berechnen, wenn ein gültiger TargetSales-Wert vorhanden ist
                    if (targetSales > 0) {
                        // Berechne die theoretischen Kosten mit dem festen Preis
                        theoreticalCosts = targetSales * fixedPrice;
                        console.log(`Selected Event ${this.selectedF1Event}: Target Sales = ${targetSales}, Fixed ${this.selectedPriceType} = ${fixedPrice}, Theoretical ${this.selectedPriceType} = ${theoreticalCosts}`);
                    }
                }
            }
            
            console.log("==== SUMMARY ====");
            console.log(`Total Theoretical ${this.selectedPriceType}:`, theoreticalCosts);
            
            // Wenn keine theoretischen Kosten berechnet werden konnten, können wir kein Chart anzeigen
            if (theoreticalCosts <= 0) {
                console.log(`No theoretical ${this.selectedPriceType} could be calculated, cannot display chart`);
                    return [];
            }
            
            // Erstelle die Ergebnisdaten für das Pie Chart
            const result = [];
            
            // Füge die drei Kategorien hinzu
            const totalActualCosts = investorsCosts + partnershipSalesCosts + otherPartnersCosts;

            // 1. Investors
            if (investorsCosts > 0) {
                const percentage = (investorsCosts / theoreticalCosts) * 100;
                result.push({
                    label: 'Investors',
                    value: investorsCosts,
                    color: '#FF6384',
                    backgroundColor: '#FF6384'
                });
                console.log(`Added to chart: Investors, ${this.selectedPriceType} = ${investorsCosts}, Percentage = ${percentage.toFixed(1)}%`);
            }

            // 2. Partnership Sales
            if (partnershipSalesCosts > 0) {
                const percentage = (partnershipSalesCosts / theoreticalCosts) * 100;
                result.push({
                    label: 'Partnership Sales',
                    value: partnershipSalesCosts,
                    color: '#36A2EB',
                    backgroundColor: '#36A2EB'
                });
                console.log(`Added to chart: Partnership Sales, ${this.selectedPriceType} = ${partnershipSalesCosts}, Percentage = ${percentage.toFixed(1)}%`);
            }

            // 3. Other Partners
            if (otherPartnersCosts > 0) {
                const percentage = (otherPartnersCosts / theoreticalCosts) * 100;
                result.push({
                    label: 'Partners',
                    value: otherPartnersCosts,
                    color: '#FFCE56',
                    backgroundColor: '#FFCE56'
                });
                console.log(`Added to chart: Partners, ${this.selectedPriceType} = ${otherPartnersCosts}, Percentage = ${percentage.toFixed(1)}%`);
            }

            // Füge die verbleibenden theoretischen Kosten hinzu (wenn nicht alle Target Sales erreicht wurden)
            const remainingCosts = theoreticalCosts - totalActualCosts;
            console.log(`Remaining ${this.selectedPriceType} (Theoretical - Actual): ${remainingCosts}`);

            // Prüfe, ob die tatsächlichen Kosten höher sind als die theoretischen Kosten
            if (totalActualCosts > theoreticalCosts) {
                console.log(`==== EXCESS SCENARIO ====`);
                console.log(`Actual ${this.selectedPriceType} (${totalActualCosts}) exceed theoretical ${this.selectedPriceType} (${theoreticalCosts}) by ${totalActualCosts - theoreticalCosts}`);
                
                // Wir haben zwei Möglichkeiten, je nachdem wie viele Partner-Kategorien wir haben
                const resultWithExcess = [];
                
                if (result.length <= 1) {
                    // Variante 1: Nur eine Partner-Kategorie oder keine
                    console.log("Using single segment display with excess");
                    
                    // Berechne den Prozentsatz der Überschreitung
                    const excessPercentage = ((totalActualCosts - theoreticalCosts) / theoreticalCosts) * 100;
                    
                    // Segment 1: 100% der Target-Costs erreicht (in der Partnerfarbe)
                    resultWithExcess.push({
                        label: 'Target Reached',
                        value: theoreticalCosts,
                        color: '#36A2EB',
                        backgroundColor: '#36A2EB'
                    });
                    console.log(`Added segment: Target Reached, Value = ${theoreticalCosts}, Percentage = 100.0%`);
                    
                    // Segment 2: Überschreitung (in einer anderen Farbe)
                    resultWithExcess.push({
                        label: 'Excess',
                        value: totalActualCosts - theoreticalCosts,
                        color: '#4a6fa5',
                        backgroundColor: '#4a6fa5'
                    });
                    console.log(`Added segment: Excess, Value = ${totalActualCosts - theoreticalCosts}, Percentage = ${excessPercentage.toFixed(1)}%`);
                } else {
                    // Variante 2: Mehrere Partner-Kategorien
                    console.log("Using multi-segment display with excess");
                    
                    // Berechne den Skalierungsfaktor für die Partner-Kategorien
                    const scaleFactor = theoreticalCosts / totalActualCosts;
                    console.log(`Scale factor for partner categories: ${scaleFactor}`);
                    
                    // Füge die skalierten Partner-Kategorien hinzu
                    result.forEach(item => {
                        const scaledValue = item.value * scaleFactor;
                        const percentage = (scaledValue / theoreticalCosts) * 100;
                        
                        resultWithExcess.push({
                            label: item.label.split(' (')[0],
                            value: scaledValue,
                            color: item.color,
                            backgroundColor: item.backgroundColor
                        });
                        console.log(`Added scaled segment: ${item.label.split(' (')[0]}, Original Value = ${item.value}, Scaled Value = ${scaledValue}, Percentage = ${percentage.toFixed(1)}%`);
                    });
                    
                    // Berechne den Prozentsatz der Überschreitung
                    const excessPercentage = ((totalActualCosts - theoreticalCosts) / theoreticalCosts) * 100;
                    
                    // Füge das Überschreitungs-Segment hinzu
                    resultWithExcess.push({
                        label: 'Excess',
                        value: totalActualCosts - theoreticalCosts,
                        color: '#4a6fa5',
                        backgroundColor: '#4a6fa5'
                    });
                    console.log(`Added segment: Excess, Value = ${totalActualCosts - theoreticalCosts}, Percentage = ${excessPercentage.toFixed(1)}%`);
                }
                
                this.totalValue = totalActualCosts; // Der Gesamtwert bleibt die tatsächlichen Kosten
                
                console.log("==== FINAL CHART DATA (WITH EXCESS) ====");
                console.log("Chart segments:", resultWithExcess.length);
                resultWithExcess.forEach((item, index) => {
                    console.log(`Segment ${index + 1}: ${item.label}, Value = ${item.value}`);
                });
                console.log("Total Value for Chart Title:", this.totalValue);
                
                // Erhöhe den chartKey, um eine vollständige Neuinitialisierung zu erzwingen
                this.chartKey++;
                
                return resultWithExcess;
            } else {
                console.log(`==== NORMAL SCENARIO (ACTUAL ≤ THEORETICAL) ====`);
                this.totalValue = theoreticalCosts;
                
                // Füge das "Open Target Sales"-Segment nur hinzu, wenn tatsächlich ein Unterschied besteht
                if (remainingCosts > 0) {
                    const remainingPercentage = (remainingCosts / theoreticalCosts) * 100;
                    result.push({
                        label: 'Open Target Sales',
                        value: remainingCosts,
                        color: '#C9CBCF',
                        backgroundColor: '#C9CBCF'
                    });
                    console.log(`Added segment: Open Target Sales, Value = ${remainingCosts}, Percentage = ${remainingPercentage.toFixed(1)}%`);
                }
            }
            
            console.log("==== FINAL CHART DATA (NORMAL) ====");
            console.log("Chart segments:", result.length);
            result.forEach((item, index) => {
                console.log(`Segment ${index + 1}: ${item.label}, Value = ${item.value}`);
            });
            console.log("Total Value for Chart Title:", this.totalValue);
            
            // Erhöhe den chartKey, um eine vollständige Neuinitialisierung zu erzwingen
            this.chartKey++;
            
            return result;
        },
        summary() {
            const valuesArray = Array.isArray(this.values) ? this.values : [];
            const totalAllValue = valuesArray.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
            const unusedItem = valuesArray.find(item =>
                typeof item.label === 'string' && (/open target sales/i.test(item.label) || /unused/i.test(item.label))
            );
            const unusedValue = unusedItem ? (parseFloat(unusedItem.value) || 0) : 0;
            const usedValue = totalAllValue - unusedValue;
            return {
                all: totalAllValue,
                unused: unusedValue,
                used: usedValue
            };
        },
        isMoneyValue() {
            // Setze isMoneyValue auf true, damit die Werte als Geldbeträge formatiert werden
            return true;
        }
    },
    methods: {
        ...mapActions([
            'getEventTargets'
        ]),
        // Speichere den ausgewählten Preistyp in der Widget-Konfiguration
        savePriceTypeToConfig() {
            if (this.widget.data) {
                this.$set(this.widget.data, 'priceType', this.selectedPriceType);
            }
        },
        formatDisplayValue(value) {
            const numericValue = parseFloat(value) || 0;
            if (this.isMoneyValue) {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2
                }).format(numericValue);
            }
            return new Intl.NumberFormat('en-US', {
                maximumFractionDigits: 0
            }).format(numericValue);
        }
    },
    watch: {
        // Aktualisiere das Chart, wenn sich der Preistyp ändert
        selectedPriceType(newValue) {
            this.chartKey++; // Erzwinge ein Neuladen des Charts
            this.savePriceTypeToConfig();
        }
    }
};
</script>

<style>
.chart-wrapper {
    position: relative;
    height: auto;
    width: 100%;
}

.chart-summary {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}
.summary-row {
    display: flex;
    gap: 6px;
}
.summary-label {
    font-weight: 600;
}
.summary-value {
    font-variant-numeric: tabular-nums;
}
</style> 